import type { DeadlineRecurrenceFilter } from '@prospero/shared';
import type {
  ConsultantDeadlineBucket,
  ConsultantDeadlineDomain,
} from '@shared/dto/deadline/consultant-deadline.dto';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useInjection } from 'inversify-react';
import { ConsultantDeadlinesService } from '../../services/consultant/consultant-deadlines.service';
import type { IConsultantDeadlinesService } from '../../services/consultant/consultant-deadlines.service.interface';
import { useAuthUser } from '../use-auth-user';

export const CONSULTANT_DEADLINES_SUMMARY_PAGE_SIZE = 50;
export const CONSULTANT_DEADLINE_ROWS_PAGE_SIZE = 100;
export const CONSULTANT_DEADLINE_CALENDAR_PAGE_SIZE = 200;

export interface ConsultantDeadlineRowFilters {
  domain: ConsultantDeadlineDomain | '';
  bucket: ConsultantDeadlineBucket | '';
  recurrence: DeadlineRecurrenceFilter | '';
  documentTypeId: string;
  /** Testo libero su titolo e tipo documento. */
  document: string;
  dueFrom: string;
  dueTo: string;
  /** Filtro persona: ha senso solo con un cliente selezionato; `''` = tutte. */
  assigneeUserId: string;
  /** Vista delle archiviate e chiuse: righe e contatori contano solo quelle. */
  archived: boolean;
}

type CalendarFilters = Pick<
  ConsultantDeadlineRowFilters,
  'domain' | 'bucket' | 'recurrence' | 'documentTypeId' | 'document' | 'assigneeUserId'
>;

const NO_ROW_FILTERS: ConsultantDeadlineRowFilters = {
  domain: '',
  bucket: '',
  recurrence: '',
  documentTypeId: '',
  document: '',
  dueFrom: '',
  dueTo: '',
  assigneeUserId: '',
  archived: false,
};

/** Every key starts with the consultant id: the cache never mixes two consultants and never depends on the current tenant. */
export const consultantDeadlineQueryKeys = {
  all: (consultantUserId: string) => ['consultant-deadlines', consultantUserId] as const,
  summary: (
    consultantUserId: string,
    page: number,
    pageSize: number,
    search: string,
    tenantId: string,
    filters: ConsultantDeadlineRowFilters = NO_ROW_FILTERS,
  ) =>
    [
      'consultant-deadlines',
      consultantUserId,
      'summary',
      page,
      pageSize,
      search,
      tenantId,
      filters.domain,
      filters.bucket,
      filters.recurrence,
      filters.documentTypeId,
      filters.document,
      filters.dueFrom,
      filters.dueTo,
      filters.archived,
    ] as const,
  rows: (consultantUserId: string, tenantId: string, filters: ConsultantDeadlineRowFilters) =>
    [
      'consultant-deadlines',
      consultantUserId,
      'rows',
      tenantId,
      filters.domain,
      filters.bucket,
      filters.recurrence,
      filters.documentTypeId,
      filters.document,
      filters.dueFrom,
      filters.dueTo,
      filters.assigneeUserId,
      filters.archived,
    ] as const,
  calendar: (
    consultantUserId: string,
    from: string,
    to: string,
    tenantId: string,
    filters: CalendarFilters,
  ) =>
    [
      'consultant-deadlines',
      consultantUserId,
      'calendar',
      from,
      to,
      tenantId,
      filters.domain,
      filters.bucket,
      filters.recurrence,
      filters.documentTypeId,
      filters.document,
      filters.assigneeUserId,
    ] as const,
  digestStatus: (consultantUserId: string) =>
    ['consultant-deadlines', consultantUserId, 'digest-status'] as const,
  validitySuggestion: (consultantUserId: string, documentTypeId: string) =>
    ['consultant-deadlines', consultantUserId, 'validity-suggestion', documentTypeId] as const,
  sameTypeSummary: (
    consultantUserId: string,
    request: { documentTypeId: string; tenantIds: string; assigneeUserId: string },
  ) =>
    [
      'consultant-deadlines',
      consultantUserId,
      'same-type-summary',
      request.documentTypeId,
      request.tenantIds,
      request.assigneeUserId,
    ] as const,
};

export function useConsultantUserId(): string {
  return useAuthUser()?.sub ?? '';
}

function emptyToUndefined<Value extends string>(value: Value | ''): Value | undefined {
  return value === '' ? undefined : value;
}

export function useConsultantDeadlineSummary(filters: {
  page: number;
  search: string;
  tenantId: string;
  rowFilters?: ConsultantDeadlineRowFilters;
  enabled?: boolean;
}) {
  const service = useInjection<IConsultantDeadlinesService>(ConsultantDeadlinesService);
  const consultantUserId = useConsultantUserId();
  const rowFilters = filters.rowFilters ?? NO_ROW_FILTERS;

  const { data, isLoading, isPlaceholderData, isError, refetch } = useQuery({
    queryKey: consultantDeadlineQueryKeys.summary(
      consultantUserId,
      filters.page,
      CONSULTANT_DEADLINES_SUMMARY_PAGE_SIZE,
      filters.search,
      filters.tenantId,
      rowFilters,
    ),
    queryFn: () =>
      service.getSummary({
        page: filters.page,
        pageSize: CONSULTANT_DEADLINES_SUMMARY_PAGE_SIZE,
        search: emptyToUndefined(filters.search.trim()),
        tenantId: emptyToUndefined(filters.tenantId),
        domain: emptyToUndefined(rowFilters.domain),
        bucket: emptyToUndefined(rowFilters.bucket),
        recurrence: emptyToUndefined(rowFilters.recurrence),
        documentTypeId: emptyToUndefined(rowFilters.documentTypeId),
        document: emptyToUndefined(rowFilters.document.trim()),
        dueFrom: emptyToUndefined(rowFilters.dueFrom),
        dueTo: emptyToUndefined(rowFilters.dueTo),
        archived: rowFilters.archived || undefined,
      }),
    enabled: consultantUserId !== '' && (filters.enabled ?? true),
    placeholderData: keepPreviousData,
  });

  return {
    tenantSummaries: data?.items ?? [],
    total: data?.total ?? 0,
    pageSize: CONSULTANT_DEADLINES_SUMMARY_PAGE_SIZE,
    isLoading,
    isPlaceholderData,
    isError,
    refetch,
  };
}

/** Mounted only while a client group is expanded: that is the lazy loading. */
export function useConsultantDeadlineRows(tenantId: string, filters: ConsultantDeadlineRowFilters) {
  const service = useInjection<IConsultantDeadlinesService>(ConsultantDeadlinesService);
  const consultantUserId = useConsultantUserId();

  const { data, isLoading, isError, refetch, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: consultantDeadlineQueryKeys.rows(consultantUserId, tenantId, filters),
      initialPageParam: 1,
      queryFn: ({ pageParam }) =>
        service.listDeadlines({
          page: pageParam,
          pageSize: CONSULTANT_DEADLINE_ROWS_PAGE_SIZE,
          tenantId,
          domain: emptyToUndefined(filters.domain),
          bucket: emptyToUndefined(filters.bucket),
          recurrence: emptyToUndefined(filters.recurrence),
          documentTypeId: emptyToUndefined(filters.documentTypeId),
          document: emptyToUndefined(filters.document.trim()),
          assigneeUserId: emptyToUndefined(filters.assigneeUserId),
          dueFrom: emptyToUndefined(filters.dueFrom),
          dueTo: emptyToUndefined(filters.dueTo),
          archived: filters.archived || undefined,
        }),
      getNextPageParam: (lastPage, loadedPages) =>
        loadedPages.length * CONSULTANT_DEADLINE_ROWS_PAGE_SIZE < lastPage.total
          ? loadedPages.length + 1
          : undefined,
      enabled: consultantUserId !== '',
    });

  return {
    deadlineRows: data?.pages.flatMap((rowsPage) => rowsPage.items) ?? [],
    total: data?.pages[0]?.total ?? 0,
    isLoading,
    isError,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  };
}

export function useConsultantDeadlineCalendar(
  range: { from: string; to: string },
  tenantId: string,
  filters: CalendarFilters,
) {
  const service = useInjection<IConsultantDeadlinesService>(ConsultantDeadlinesService);
  const consultantUserId = useConsultantUserId();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: consultantDeadlineQueryKeys.calendar(
      consultantUserId,
      range.from,
      range.to,
      tenantId,
      filters,
    ),
    queryFn: () =>
      service.listDeadlines({
        page: 1,
        pageSize: CONSULTANT_DEADLINE_CALENDAR_PAGE_SIZE,
        tenantId: emptyToUndefined(tenantId),
        domain: emptyToUndefined(filters.domain),
        bucket: emptyToUndefined(filters.bucket),
        recurrence: emptyToUndefined(filters.recurrence),
        documentTypeId: emptyToUndefined(filters.documentTypeId),
        document: emptyToUndefined(filters.document.trim()),
        assigneeUserId: emptyToUndefined(filters.assigneeUserId),
        dueFrom: range.from,
        dueTo: range.to,
      }),
    enabled: consultantUserId !== '',
    placeholderData: keepPreviousData,
  });

  return { deadlineRows: data?.items ?? [], total: data?.total ?? 0, isLoading, isError, refetch };
}

export function useConsultantDigestStatus() {
  const service = useInjection<IConsultantDeadlinesService>(ConsultantDeadlinesService);
  const consultantUserId = useConsultantUserId();

  const { data, isLoading } = useQuery({
    queryKey: consultantDeadlineQueryKeys.digestStatus(consultantUserId),
    queryFn: () => service.getDigestStatus(),
    enabled: consultantUserId !== '',
  });

  return { digestStatus: data, isLoading };
}
